// cSpell:ignore betaling Eigenaar Erelonen Gedeeld Huurder Onderhoud onmiddellijke panden Volgende
import {
  Category,
  CATEGORY_META,
  Invoice,
  isOwnerOnlyCategory,
  settlementEligible,
} from "./types";

const FULL_SHARE = 100;

export type TProposedCharge =
  | { target: "owner" | "tenant" }
  | { target: "shared"; tenantSharePct: number }
  // A 0% category proposes nothing: one click on Volgende would otherwise
  // bill an owner cost to the tenant in full.
  | { target: undefined };

// Who the cost popup proposes to bear a cost of this category, from the
// category's standard tenant share (Rentio's category matrix). A partial
// standard (Erelonen 34%, Onderhoud 50%) is "Gedeeld" with that share:
// proposing "Huurder" billed the tenant for all of it. An ownership cost is
// always the owner's.
export const proposedChargeFor = (category: Category): TProposedCharge => {
  if (isOwnerOnlyCategory(category)) return { target: "owner" };
  const standard = CATEGORY_META[category]?.defaultTenantSharePct ?? FULL_SHARE;
  if (standard <= 0) return { target: undefined };
  if (standard < FULL_SHARE) {
    return { target: "shared", tenantSharePct: standard };
  }
  return { target: "tenant" };
};

// The cost as the settlement bills it. An older ownership cost that still
// names the tenant goes to the owner (computeResult), so the cost list, the
// cost detail and the export say "Eigenaar" too. A payment that was already
// asked from the tenant, or a cost marked paid, stays what it was: such a
// cost is not up for settlement.
export const withBilledTarget = <TInvoice extends Invoice>(
  invoice: TInvoice,
): TInvoice =>
  isOwnerOnlyCategory(invoice.category) &&
  invoice.target !== "owner" &&
  settlementEligible(invoice)
    ? { ...invoice, target: "owner", tenantSharePct: undefined }
    : invoice;

// Whether the cost popup knows who rents the chosen panden in the cost's
// period, so that "no tenant" means vacancy: once the leases of the chosen
// unit(s) have arrived, and for a building once its units and their leases
// have. While a fetch is in flight the lists are empty, which reads as
// vacancy: "onmiddellijke betaling" was greyed out as if nobody rented the
// unit, and a tenant cost was stored as the owner's.
export const isCostOccupancyKnown = (params: {
  isBuildingCost: boolean;
  // A unit cost: the leases of the chosen unit(s).
  areLeasesLoaded: boolean;
  // A building cost: its units, and their leases.
  isFetchingBuildingUnits: boolean;
  buildingUnitCount: number;
  areBuildingLeasesLoaded: boolean;
}): boolean => {
  const {
    isBuildingCost,
    areLeasesLoaded,
    isFetchingBuildingUnits,
    buildingUnitCount,
    areBuildingLeasesLoaded,
  } = params;
  if (!isBuildingCost) return areLeasesLoaded;
  return (
    !isFetchingBuildingUnits && buildingUnitCount > 0 && areBuildingLeasesLoaded
  );
};
